import dateUtil from '../../../utils/dateUtil';
import { worksheetToAOA } from '../../../utils/excelHelper';

/* ── ExcelJS lazy loader ── */
let __exceljsPromise: Promise<typeof import('exceljs')> | null = null;
export async function ensureExcelJS() {
  if (!__exceljsPromise) {
    __exceljsPromise = import('exceljs');
  }
  return __exceljsPromise;
}

/* ── Shared interfaces ── */
export interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

export interface BreakPeriod {
  id?: number;
  start_time: string;
  end_time: string;
  [key: string]: any;
}

export interface TimeTracking {
  id: number;
  employee: number;
  work_area: number;
  work_area__name?: string;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  breakperiods?: BreakPeriod[];
  [key: string]: any;
}

export interface PreviewSummary {
  okNew: number;
  okCr: number;
  warn: number;
  error: number;
  skipped: number;
}

export type NormalizedImport = {
  rowIndex: number;
  employeeId: number;
  employeeName?: string;
  workAreaId: number | null;
  workAreaName?: string;
  day: string;
  startHm: string;
  endHm: string;
  startIsoUtc?: string;
  endIsoUtc?: string;
  ttId?: number | null;
  type: 'NEW' | 'CR';
  error?: string;
  key?: string;
  breakMinutes?: number | null;
};

/* ── Header / mapping helpers ── */
export const normalizeHeader = (h: string) => (h || '').toLowerCase().trim();

export const headerKeyMap: Record<string, string> = {
  'employee id': 'employee_id',
  'employee': 'employee_name',
  'employee name': 'employee_name',
  'work area': 'work_area',
  'day': 'day',
  'date': 'day',
  'start': 'start',
  'end': 'end',
  'break (min)': 'break_min',
  'break': 'break_min',
  'net (min)': 'net_min',
  'net (hh:mm)': 'net_hhmm',
  'tt id': 'tt_id',
  'id': 'tt_id',
};

export function buildMapping(header: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  header.forEach((h, idx) => {
    const key = headerKeyMap[normalizeHeader(h)] || '';
    if (key) map[key] = String(idx);
  });
  return map;
}

export function isSampleRow(row: any, map: Record<string, string>): boolean {
  const ttIdx = map.tt_id !== undefined ? Number(map.tt_id) : -1;
  if (ttIdx >= 0) {
    const v = String(row[ttIdx] ?? '').toUpperCase();
    if (v.includes('SAMPLE')) return true;
  }
  const first = String(row[0] ?? '').toUpperCase();
  if (first.includes('INFO')) return true;
  return false;
}

/* ── CSV parser ── */
export function parseCSVText(text: string): string[][] {
  const sep =
    (text.split('\n')[0].match(/;/g)?.length || 0) >=
    (text.split('\n')[0].match(/,/g)?.length || 0)
      ? ';'
      : ',';
  const rows: string[][] = [];
  let i = 0,
    field = '',
    inQuotes = false,
    row: string[] = [];
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === sep) {
        row.push(field);
        field = '';
      } else if (c === '\n' || c === '\r') {
        if (field.length > 0 || row.length > 0) {
          row.push(field);
          rows.push(row);
          row = [];
          field = '';
        }
      } else {
        field += c;
      }
    }
    i++;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.length > 0 && r.some((v) => String(v).trim() !== ''));
}

/* ── Excel file parser ── */
export async function parseExcelFile(file: File): Promise<any[][]> {
  const ExcelJS = await ensureExcelJS();
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  return ws ? worksheetToAOA(ws) : [];
}

/* ── Date / time cell parsers ── */
function excelSerialToDate(serial: number): Date {
  const utcDays = Math.floor(serial - 25569);
  const utcMs = utcDays * 86400 * 1000;
  const frac = serial - Math.floor(serial);
  const dayMs = Math.round(frac * 86400 * 1000);
  return new Date(utcMs + dayMs);
}

function toStr(v: any): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function parseDayCell(val: any): string {
  if (typeof val === 'number') {
    const d = excelSerialToDate(val);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }
  const s = toStr(val);
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\.\/\-](\d{1,2})[\.\/\-](\d{2,4})$/);
  if (m) {
    const dd = m[1].padStart(2, '0');
    const mm = m[2].padStart(2, '0');
    const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  const d2 = new Date(s);
  if (!isNaN(d2.getTime())) return d2.toISOString().slice(0, 10);
  return '';
}

function parseTimeCell(val: any): string {
  if (typeof val === 'number') {
    const frac = val % 1;
    const totalSec = Math.round((frac < 0 ? frac + 1 : frac) * 86400);
    const hh = String(Math.floor(totalSec / 3600) % 24).padStart(2, '0');
    const mm = String(Math.floor(totalSec / 60) % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  let s = toStr(val)
    .replace('.', ':')
    .replace('-', ':');
  if (!s) return '';
  const ampm = s.match(/^(\d{1,2}):(\d{1,2})\s*([AP]M)$/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const mn = Number(ampm[2]);
    const ap = ampm[3].toUpperCase();
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
  }
  const hms = s.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (hms) {
    const h = String(Number(hms[1])).padStart(2, '0');
    const mn = String(Number(hms[2])).padStart(2, '0');
    return `${h}:${mn}`;
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return '';
}

/* ── Row normalization ── */
export function normalizeRows(
  rows: any[],
  mapping: Record<string, string>,
  workAreas: WorkArea[]
): NormalizedImport[] {
  const out: NormalizedImport[] = [];
  const seenKeys = new Set<string>();
  rows.forEach((r, idx) => {
    const atIndex = (name: string) => (mapping[name] !== undefined ? Number(mapping[name]) : -1);
    const atRaw = (name: string) => {
      const i = atIndex(name);
      return i >= 0 ? r[i] : '';
    };
    const at = (name: string) => toStr(atRaw(name));
    const normHm = (s: string) =>
      s
        .replace('.', ':')
        .replace('-', ':');

    const empIdStr = at('employee_id');
    const empId = empIdStr ? Number(empIdStr) : NaN;
    const empName = at('employee_name') || undefined;

    const waCellRaw = at('work_area');
    let waId: number | null = null;
    let waName: string | undefined = undefined;
    if (waCellRaw) {
      const waCell = waCellRaw.trim();
      const m = waCell.match(/^(\d+)(?:\s*\((.+?)\))?$/);
      if (m) {
        waId = Number(m[1]);
        const waObj = workAreas.find((w) => w.id === waId);
        waName = waObj?.name || m[2];
      } else {
        const waObj = workAreas.find(
          (w) => (w.name || '').trim().toLowerCase() === waCell.toLowerCase()
        );
        if (waObj) {
          waId = waObj.id;
          waName = waObj.name;
        }
      }
    }

    const day = parseDayCell(atRaw('day'));
    const startHm = parseTimeCell(atRaw('start'));
    const endHm = parseTimeCell(atRaw('end'));
    const brkStr = at('break_min');
    const breakMinutes = brkStr ? Number(brkStr) : null;

    const ttCell = atRaw('tt_id');
    let ttId: number | null = null;
    if (ttCell !== '' && ttCell !== null && ttCell !== undefined) {
      if (typeof ttCell === 'number' && Number.isFinite(ttCell)) {
        ttId = Math.trunc(ttCell);
      } else {
        const s = String(ttCell).trim();
        const m2 = s.match(/\d+/);
        if (m2) ttId = Number(m2[0]);
      }
    }

    let error: string | undefined;
    if (!empIdStr || isNaN(empId)) error = 'Missing/invalid Employee ID';
    const sHm = normHm(startHm);
    const eHm = normHm(endHm);
    if (!day || !sHm || !eHm) error = (error ? error + '; ' : '') + 'Missing day/start/end';
    if (waId === null) error = (error ? error + '; ' : '') + 'Unknown Work Area';

    let startIsoUtc: string | undefined;
    let endIsoUtc: string | undefined;
    if (!error) {
      const sIso = dateUtil.createTenantTimeAsUTC(day, sHm);
      const eIso0 = dateUtil.createTenantTimeAsUTC(day, eHm);
      let eIso = eIso0;
      if (sIso && eIso0) {
        if (new Date(eIso0) < new Date(sIso)) {
          const d = new Date(eIso0);
          d.setDate(d.getDate() + 1);
          eIso = d.toISOString();
        }
        startIsoUtc = sIso || undefined;
        endIsoUtc = eIso || undefined;
        if (startIsoUtc && !/[zZ]|[+-]\d{2}:\d{2}$/.test(startIsoUtc)) startIsoUtc += 'Z';
        if (endIsoUtc && !/[zZ]|[+-]\d{2}:\d{2}$/.test(endIsoUtc)) endIsoUtc += 'Z';
        const grossMin = Math.round(
          (new Date(endIsoUtc).getTime() - new Date(startIsoUtc).getTime()) / 60000
        );
        if (breakMinutes !== null && breakMinutes > grossMin)
          error = (error ? error + '; ' : '') + 'Break exceeds gross duration';
      } else {
        error = (error ? error + '; ' : '') + 'Unparseable date/time';
      }
    }

    const type: 'NEW' | 'CR' = ttId ? 'CR' : 'NEW';
    const key = `${empId}|${waId}|${day}|${startHm}|${endHm}`;
    if (seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    out.push({
      rowIndex: idx + 1,
      employeeId: empId,
      employeeName: empName,
      workAreaId: waId,
      workAreaName: waName,
      day,
      startHm: sHm,
      endHm: eHm,
      startIsoUtc,
      endIsoUtc,
      ttId,
      type,
      error,
      key,
      breakMinutes,
    });
  });

  const lastCrByTt = new Map<number, NormalizedImport>();
  out.forEach((n) => {
    if (n.type === 'CR' && n.ttId) lastCrByTt.set(n.ttId, n);
  });
  return out.filter((n) => n.type !== 'CR' || !n.ttId || lastCrByTt.get(n.ttId) === n);
}

/* ── UTC helpers ── */
export function toUTCIfNaiveLocal(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  if (hasTz) return iso;
  const dStr = dateUtil.extractDateFromISO(iso);
  const tStr = dateUtil.extractTimeFromISO(iso);
  const utc = dStr && tStr ? dateUtil.createTenantTimeAsUTC(dStr, tStr) : null;
  return utc || iso;
}

export function normalizeUTC(iso?: string | null): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(iso)) {
    return `${iso}Z`;
  }
  return iso;
}

export function ensureZ(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  return /[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
}

export function sameMinute(a?: string, b?: string): boolean {
  const toComparableMinute = (raw?: string): number | null => {
    if (!raw) return null;
    let s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(s)) {
      s = s.replace(' ', 'T');
    }
    s = s.replace(/(\.\d{3})\d+/, '$1');
    const normalized2 = toUTCIfNaiveLocal(s) || normalizeUTC(s) || s;
    let d = new Date(normalized2);
    if (Number.isNaN(d.getTime())) {
      const dStr = dateUtil.extractDateFromISO(s);
      const tStr = dateUtil.extractTimeFromISO(s);
      const iso2 = dStr && tStr ? dateUtil.createTenantTimeAsUTC(dStr, tStr) : null;
      if (!iso2) return null;
      d = new Date(iso2);
      if (Number.isNaN(d.getTime())) return null;
    }
    return Math.floor(d.getTime() / 60000);
  };
  const A = toComparableMinute(a);
  const B = toComparableMinute(b);
  return A !== null && B !== null && A === B;
}

export function getExistingBreakMin(tt: TimeTracking): number {
  if (!tt.breakperiods || tt.breakperiods.length === 0) return 0;
  let total = 0;
  tt.breakperiods.forEach((b) => {
    if (b.start_time && b.end_time) {
      const bs = new Date(b.start_time);
      const be = new Date(b.end_time);
      if (!isNaN(bs.getTime()) && !isNaN(be.getTime()) && be > bs) {
        total += Math.round((be.getTime() - bs.getTime()) / 60000);
      }
    }
  });
  return total;
}

export function getWA(tt: TimeTracking): number {
  return (tt as any).work_area ?? tt.work_area;
}

export function isUnchanged(n: NormalizedImport, tt: TimeTracking): boolean {
  const timesSame = sameMinute(n.startIsoUtc, tt.start_time) && sameMinute(n.endIsoUtc, tt.end_time);
  const waSame = n.workAreaId == null ? true : n.workAreaId === getWA(tt);
  let breakSame = true;
  if (n.breakMinutes != null) {
    const cur = getExistingBreakMin(tt);
    breakSame = Number(n.breakMinutes) === Number(cur);
  }
  return timesSame && waSame && breakSame;
}

export function short(s: any, max = 400): string {
  try {
    const t = typeof s === 'string' ? s : JSON.stringify(s);
    return t.length > max ? t.slice(0, max) + '\u2026' : t;
  } catch {
    return String(s ?? '');
  }
}

/* ── Form download helpers ── */
export function buildHeader(): string[] {
  return [
    'Employee ID',
    'Employee Name',
    'Work Area',
    'Day',
    'Start',
    'End',
    'Break (min)',
    'Net (min)',
    'Net (hh:mm)',
    'TT ID',
  ];
}

export function buildSamples(waList?: string): any[][] {
  return [
    ['(INFO)', 'Samples \u2013 will not be imported', waList || '', '', '', '', '', '', '', ''],
    ['SAMPLE-EMP', 'SAMPLE-NAME', '', '2025-09-01', '09:00', '17:30', '30', '', '', 'SAMPLE1'],
    ['SAMPLE-EMP', 'SAMPLE-NAME', '', '2025-09-02', '22:00', '06:00', '30', '', '', 'SAMPLE2'],
  ];
}

export function buildBlankRowFor(empId: number, empName: string): any[] {
  return [empId, empName, '', '', '', '', '', '', '', ''];
}
